import { User } from '../auth/User';
import { ApiRoutes, getFullApiRoute } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

/**
 * Interface representing a role from the API
 */
export interface IRole {
  id: string;
  tenantId: string;
  name: string;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  activeUsers?: number;
  totalUsers?: number;
  permissions?: any[]; // Module permissions from API
  advancedFeatures?: any[]; // Advanced permissions from API
}

/**
 * Interface for creating a new role
 */
export interface CreateRoleRequest {
  name: string;
  tenantId: string | null;
  isActive: boolean;
  description?: string;
  featurePermissionIds?: string[];
}

/**
 * Interface for updating an existing role
 */
export interface UpdateRoleRequest extends CreateRoleRequest {
  id: string;
}

/**
 * Metadata for paginated role responses
 */
export interface IRolesMetadata {
  totalCount: number;
  currentCount: number;
  hasPrev: boolean;
  hasNext: boolean;
  page: number;
  perPage: number;
}

/**
 * Paginated response structure for roles
 */
export interface IRolesResponse {
  data: IRole[];
  metadata: IRolesMetadata;
}

/**
 * Query parameters for fetching roles with server-side pagination
 */
export interface IRolesQueryParams {
  page?: number;
  limit?: number;
  sortField?: string;
  sortDir?: 'ASC' | 'DESC';
  search?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  isActive?: boolean;
  createdBy?: string;
}

/**
 * Response structure for available permissions API
 */
export interface IAvailablePermissionsResponse {
  permissions: any[];
  advancedFeatures: any[];
}

/**
 * Request body for role assignment endpoint (fetching roles)
 */
export interface IRoleAssignmentQuery {
  tenantIds: string[];
}

/**
 * Role info for assignment dropdowns
 */
export interface IAssignedRoleInfo {
  id: string;
  name: string;
}

/**
 * Roles grouped by tenant for assignment response
 */
export interface ITenantRolesResponse {
  tenantId: string | null;
  roles: IAssignedRoleInfo[];
}

/**
 * Response structure for role assignment (fetch) API
 */
export interface IRoleAssignmentResponse {
  data: ITenantRolesResponse[];
}

export class RoleAPI {
  /**
   * Get all roles for a specific tenant with optional pagination and filtering
   * @param user - The authenticated user
   * @param tenantId - The tenant ID to fetch roles for
   * @param queryParams - Optional query parameters for pagination, sorting, and filtering
   * @returns Promise<IRolesResponse> - Paginated roles response with metadata
   */
  static async getRolesByTenant(
    user: User,
    tenantId: string,
    queryParams?: IRolesQueryParams,
  ): Promise<IRolesResponse> {
    let url = `${getFullApiRoute(ApiRoutes.ROLES_V3)}/tenant/${tenantId}`;
    
    // Build query string if params provided
    if (queryParams) {
      const params = new URLSearchParams();
      
      if (queryParams.page) params.append('page', String(queryParams.page));
      if (queryParams.limit) params.append('limit', String(queryParams.limit));
      if (queryParams.sortField) params.append('sortField', queryParams.sortField);
      if (queryParams.sortDir) params.append('sortDir', queryParams.sortDir);
      if (queryParams.search) params.append('search', queryParams.search);
      if (queryParams.createdAtStart) params.append('createdAtStart', queryParams.createdAtStart);
      if (queryParams.createdAtEnd) params.append('createdAtEnd', queryParams.createdAtEnd);
      if (queryParams.isActive !== undefined) params.append('isActive', String(queryParams.isActive));
      if (queryParams.createdBy) params.append('createdBy', queryParams.createdBy);
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const result = await HttpRequest(url, 'GET', null, null, user);
    return result || { data: [], metadata: { totalCount: 0, currentCount: 0, hasPrev: false, hasNext: false, page: 1, perPage: 10 } };
  }

  /**
   * Get a specific role by ID
   * @param user - The authenticated user
   * @param id - The role ID
   * @returns Promise<IRole | undefined> - The role or undefined if not found
   */
  static async getRoleById(user: User, id: string): Promise<IRole | undefined> {
    const result = await HttpRequest(
      `${getFullApiRoute(ApiRoutes.ROLES_V3)}/${id}`,
      'GET',
      null,
      null,
      user
    );
    return result;
  }

  /**
   * Get available permissions for a tenant
   * @param user - The authenticated user
   * @param tenantId - The tenant ID
   * @returns Promise<IAvailablePermissionsResponse | undefined> - Available permissions and advanced features
   */
  static async getAvailablePermissions(
    user: User,
    tenantId: string
  ): Promise<IAvailablePermissionsResponse | undefined> {
    const url = `${getFullApiRoute(ApiRoutes.ROLES_V3)}/tenant/${tenantId}/available-permissions`;
    
    const result = await HttpRequest(
      url,
      'GET',
      null,
      null,
      user
    );
    
    return result;
  }

  /**
   * Create a new role
   * @param user - The authenticated user
   * @param roleData - The role data to create
   * @returns Promise<IRole> - The created role
   */
  static async createRole(user: User, roleData: CreateRoleRequest): Promise<IRole> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.ROLES_V3),
      'POST',
      roleData,
      new Headers({ 'Content-Type': 'application/json' }),
      user
    );
    return result;
  }

  /**
   * Update an existing role
   * @param user - The authenticated user
   * @param roleData - The role data to update
   * @returns Promise<IRole> - The updated role
   */
  static async updateRole(user: User, roleData: UpdateRoleRequest): Promise<IRole> {
    const result = await HttpRequest(
      `${getFullApiRoute(ApiRoutes.ROLES_V3)}/${roleData.id}`,
      'PUT',
      roleData,
      new Headers({ 'Content-Type': 'application/json' }),
      user
    );
    return result;
  }

  /**
   * Delete a role by ID
   * @param user - The authenticated user
   * @param id - The role ID to delete
   * @returns Promise<number> - Number of deleted records
   */
  static async deleteRole(user: User, id: string): Promise<number> {
    const result = await HttpRequest(
      `${getFullApiRoute(ApiRoutes.ROLES_V3)}/${id}`,
      'DELETE',
      null,
      null,
      user
    );
    return result || 0;
  }

  /**
   * Get active roles for user assignment dropdowns (company-level + internal).
   * Calls POST /api/v3/roles/for-assignment with { tenantIds: [...] }.
   * @param user - The authenticated user
   * @param tenantIds - Array of company/tenant IDs to fetch roles for.
   *                    Use INTERNAL_SYSTEMS_COMPANY_UUID constant for internal roles, or pass actual company UUIDs.
   * @returns Promise<IRoleAssignmentResponse> — grouped by tenant ID with { id, name } only.
   */
  static async getRolesForAssignment(
    user: User,
    tenantIds: string[],
    forFilter?: boolean
  ): Promise<IRoleAssignmentResponse> {
    const url = `${getFullApiRoute(ApiRoutes.ROLES_V3)}/for-assignment`;

    const result = await HttpRequest(
      url,
      'POST',
      { tenantIds, forFilter },
      new Headers({ 'Content-Type': 'application/json' }),
      user
    );

    return (result as IRoleAssignmentResponse) || { data: [] };
  }

  /**
   * Assign a role to a user (maps userId → roleId via V3 user-roles).
   * This is for custom company-level roles, NOT the default ACCOUNT_ADMIN/ACCOUNT_USER membership levels.
   * @param user - The authenticated user making the assignment
   * @param targetUserId - The user being assigned a role
   * @param roleId - The role ID to assign
   * @returns Promise<UserRoles> — the created mapping record (or existing if already assigned)
   */
  static async assignUserRole(
    user: User,
    targetUserId: string,
    roleId: string,
  ): Promise<unknown> {
    const url = getFullApiRoute(ApiRoutes.USER_ROLES_V3);

    const result = await HttpRequest(
      url,
      'POST',
      { userId: targetUserId, roleId },
      new Headers({ 'Content-Type': 'application/json' }),
      user
    );

    return result;
  }

  /**
   * Fetch existing role assignments for a user (returns all { userId, roleId } pairs).
   * Used to determine which custom roles are already assigned.
   * @param user - The authenticated user making the request
   * @param targetUserId - The user whose roles we want to fetch
   * @returns Promise<Array<{ id: string; userId: string; roleId: string }>> — existing role mappings
   */
  static async getUserRoles(
    user: User,
    targetUserId: string,
  ): Promise<Array<{ id: string; userId: string; roleId: string; roleName: string; tenantId?: string }>> {
    const url = `${getFullApiRoute(ApiRoutes.USER_ROLES_V3)}/user/${targetUserId}`;

    const result = await HttpRequest(
      url,
      'GET',
      null,
      null,
      user
    );

    return (result as Array<{ id: string; userId: string; roleId: string; roleName: string; tenantId?: string }>) || [];
  }

  /**
   * Delete a user-role mapping by its ID.
   * @param user - The authenticated user making the request
   * @param mappingId - The user-role mapping ID to delete (from getUserRoles response)
   * @returns Promise<number> — number of deleted records
   */
  static async deleteUserRole(
    user: User,
    mappingId: string,
  ): Promise<number> {
    const url = `${getFullApiRoute(ApiRoutes.USER_ROLES_V3)}/${mappingId}`;

    const result = await HttpRequest(
      url,
      'DELETE',
      null,
      null,
      user
    );

    return (result as number) || 0;
  }
}